/* eslint-disable react/jsx-max-props-per-line */
/* eslint-disable react/jsx-sort-props */
import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import {
  Box,
  Button,
  DialogContentText,
  Stack,
  Typography,
} from '@mui/material';
import FilterListOutlinedIcon from '@mui/icons-material/FilterListOutlined';
import AddIcon from '@mui/icons-material/Add';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';

import { CustomFilterV2, DialogDefaultV2, TableV2 } from '../../../components/v2';
import * as selectorsAuth from '../../../store/auth/reducer';
import * as selectorsModuleConfiguration from '../../../store/moduleConfiguration/reducer';
import { deleteProduto, exportProduto } from '../../../store/produto/actions';
import { PRODUTO_PEDIDO_DELETE_ACCESS } from '../../../constants/modules';
import ProdutoFormulario from './ProdutoFormulario';

const hasPermission = (moduleConfigurations, authData) =>
  moduleConfigurations?.some(
    u => u.codigo === PRODUTO_PEDIDO_DELETE_ACCESS && u.userId === authData.id
  );

const EstoqueProdutosV2 = ({ deleteProduto, exportProduto, authData, moduleConfigurations }) => {
  const [open, setOpen] = useState(false);
  const [id, setId] = useState(null);
  const [localFilter, setLocalFilter] = useState({ codigo: undefined });
  const [openReport, setOpenReport] = useState(false);
  const [openExport, setOpenExport] = useState(false);
  const [reload, setReload] = useState(false);
  const [openFilter, setOpenFilter] = useState(false);
  const [forceDeleteOpen, setForceDeleteOpen] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState(null);
  const [conflictMessage, setConflictMessage] = useState('');
  const tableReloadRef = useRef(() => {});

  const canDelete = hasPermission(moduleConfigurations, authData);

  const onEdit = rowId => {
    setOpen(true);
    setId(rowId);
  };

  const onAdd = () => {
    setOpen(true);
    setId(null);
  };

  const onDelete = (rowId, reload) => {
    tableReloadRef.current = reload;
    deleteProduto(rowId, reload, {
      onVinculos409: msg => {
        setConflictMessage(msg || '');
        setPendingDeleteId(rowId);
        setForceDeleteOpen(true);
      },
    });
  };

  const handleForceDeleteClose = () => {
    setForceDeleteOpen(false);
    setPendingDeleteId(null);
    setConflictMessage('');
  };

  const handleForceDeleteConfirm = () => {
    deleteProduto(
      pendingDeleteId,
      () => {
        handleForceDeleteClose();
        tableReloadRef.current();
      },
      { force: true }
    );
  };

  const onClose = isSave => {
    setOpen(false);
    setId(null);
    if (isSave) {
      setReload(current => !current);
    }
  };

  const headerActions = [
    {
      label: 'Novo',
      onClick: onAdd,
      startIcon: <AddIcon />,
      variant: 'contained',
    },
    {
      label: 'Exportar',
      onClick: () => setOpenExport(true),
      startIcon: <FileDownloadOutlinedIcon />,
      variant: 'outlined',
    },
    {
      label: 'Relatório',
      onClick: () => setOpenReport(true),
      startIcon: <DescriptionOutlinedIcon />,
      variant: 'outlined',
    },
  ];

  return (
    <Box>
      <Stack spacing={3}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', md: 'center' }}
          spacing={2}
          sx={{ pr: 2 }}
        >
          <Typography variant="h4">Produtos</Typography>
          <Button
            variant="outlined"
            startIcon={<FilterListOutlinedIcon />}
            onClick={() => setOpenFilter(true)}
          >
            Filtros
          </Button>
        </Stack>

        <TableV2
          useApi
          fetchUrl="produtos/filter"
          filterData={localFilter}
          refreshKey={reload}
          rowHeight={56}
          headerActions={headerActions}
          columns={[
            {
              field: 'nome',
              headerName: 'Produto',
              flex: 1,
              minWidth: 200,
              renderCell: ({ row }) => (
                <span>{row.nome}<br />({row.codigo})</span>
              ),
            },
            {
              field: 'ativo',
              headerName: 'Ativo',
              width: 100,
              renderCell: ({ row }) =>
                row.ativo === 'true' || row.ativo === true ? 'Sim' : 'Não',
            },
            {
              field: 'estoqueTotal',
              headerName: 'Quantidade',
              width: 120,
              renderCell: ({ row }) => row.estoqueTotal || '0',
            },
            {
              field: 'possuiTamanho',
              headerName: 'Possui Tamanho',
              width: 150,
              renderCell: ({ row }) =>
                row.possuiTamanho === 'true' || row.possuiTamanho === true ? 'Sim' : 'Não',
            },
            {
              field: 'descricao',
              headerName: 'Descrição',
              flex: 1,
              minWidth: 200,
            },
          ]}
          onEdit={onEdit}
          onDelete={onDelete}
          canDelete={() => canDelete}
        />

        <CustomFilterV2
          variant="dialog"
          open={openFilter}
          onClose={() => setOpenFilter(false)}
          title="Filtros"
          fullWidth
          appliedFilter={localFilter}
          fields={[
            { field: 'useSelectOption', key: 'ativo', label: 'Ativo', options: [{ id: 'true', nome: 'Sim' }, { id: 'false', nome: 'Não' }], md: 4 },
            { field: 'useProduto', key: 'produtoIds', label: 'Produtos', md: 12, multiSelect: true },
          ]}
          onFilter={filterData => {
            setLocalFilter(filterData);
            setOpenFilter(false);
          }}
        />

        <ProdutoFormulario
          id={id}
          open={open}
          onClose={onClose}
        />

        <DialogDefaultV2
          open={openExport}
          handlerCancel={() => setOpenExport(false)}
          handlerConfirm={() => {
            exportProduto(localFilter, false, () => {
              setOpenExport(false);
            });
          }}
          title="Confirmação"
          confirmMessage="Sim"
          cancelMessage="Não"
        >
          <DialogContentText>Deseja exportar os produtos filtrados?</DialogContentText>
        </DialogDefaultV2>

        <DialogDefaultV2
          open={openReport}
          handlerCancel={() => setOpenReport(false)}
          handlerConfirm={() => {
            exportProduto(localFilter, true, () => {
              setOpenReport(false);
            });
          }}
          title="Confirmação"
          confirmMessage="Sim"
          cancelMessage="Não"
        >
          <DialogContentText>Deseja gerar o relatório dos produtos filtrados?</DialogContentText>
        </DialogDefaultV2>

        <DialogDefaultV2
          open={forceDeleteOpen}
          handlerCancel={handleForceDeleteClose}
          handlerConfirm={handleForceDeleteConfirm}
          title="Exclusão com vínculos"
          confirmMessage="Excluir mesmo assim"
          cancelMessage="Cancelar"
        >
          <DialogContentText>
            {conflictMessage}{' '}
            Deseja excluir o produto mesmo assim? Esta ação não poderá ser desfeita.
          </DialogContentText>
        </DialogDefaultV2>
      </Stack>
    </Box>
  );
};

EstoqueProdutosV2.propTypes = {
  deleteProduto: PropTypes.func.isRequired,
  exportProduto: PropTypes.func.isRequired,
  authData: PropTypes.object.isRequired,
  moduleConfigurations: PropTypes.array,
};

const mapStateToProps = state => ({
  authData: selectorsAuth.getAuth(state),
  moduleConfigurations: selectorsModuleConfiguration.getModuleConfigurations(state),
});

const mapDispatchToProps = dispatch => bindActionCreators({
  deleteProduto,
  exportProduto,
}, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(EstoqueProdutosV2);
